import React, { useContext, useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  Spinner,
} from "reactstrap";
import DeleteModal from "../../../../../Components/Common/DeleteModal";
import {
  perPageData,
  showToastMessage,
} from "../../../../../common/service/CommonService";
import Reason from "../../../../../common/utilities/Reason/Reason";
import CommonTableIndexWise from "../../../../../Components/Common/CommonTableIndexWise";
import { useStudyScreeningService } from "../api/useStudyScreeningQuery";
import { queryClient } from "../../../../../api/queryClient";
import ScreeningDecisionModal from "../components/ScreeningDecisionModal";
import AddEditScreeningModal from "../components/AddEditScreeningModal";
import CRFScreeningModal from "../components/CRFScreeningModal";
import { useNavigate } from "react-router-dom";
import sf from "assets/scss/common/searchFilter.module.scss";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "../../../../../common/constants/PermissionConstants";
import CommonButton from "../../../../../Components/Common/CommonButton";
import FilePreviewModal from "../../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { ScreeningFormValues, ScreeningMaster } from "../helper/studyscreening.types";
import { getScreeningColumns } from "../helper/studyscreening.constant";
import { STUDY_PATHS } from "Routes/paths";
import { BreadcrumbContext } from "Layouts/Header";
import { usePermission } from "context/PermissionContext";
import { useCRFService } from "modules/Library/CRF/api/useCRFQuery";

/* ═══════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════ */

export interface StudyScreeningListProps {
  readonly?: boolean;
}

const StudyScreeningList: React.FC<StudyScreeningListProps> = ({ readonly }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [isAddLoading, setIsAddLoading] = useState(false);

  const { AssignCRFSelectAllQuery } = useCRFService({
    modulename: "Study Screening",
    pageNumber: 1,
    pageSize: 1000,
  });

  //-------------------------------------------------------------------------------------------
  //--> Use State
  //-------------------------------------------------------------------------------------------

  const [isOpen, setIsOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ScreeningMaster | null>(
    null,
  );
  const [editRecord, setEditRecord] = useState<ScreeningMaster | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitHandler, setSubmitHandler] = useState<(() => void) | null>(null);
  const [isCrfOpen, setIsCrfOpen] = useState(false);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [selectedCrf, setSelectedCrf] = useState<any | null>(null);
  const [updatedRecord, setUpdatedRecord] =
    useState<ScreeningFormValues | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  //Audit
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  //Reason Modal
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  // Decision Modal State
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [selectedScreening, setSelectedScreening] =
    useState<ScreeningMaster | null>(null);
  const [decisionStatus, setDecisionStatus] = useState<
    "passed" | "failed" | null
  >(null);
  const [reasonforunfit, setReasonforunfit] = useState<string>("");

  // Decision Reason Model
  const [isDecisionReasonOpen, setIsDecisionReasonOpen] = useState(false);
  const [pendingDecisionPayload, setPendingDecisionPayload] = useState<{
    id: number;
    screeningstatus: "passed" | "failed";
    volunteerid: number;
    studyid: number;
    reasonforunfit: string | null;
  } | null>(null);

  // --- API DATA ---
  const volunteerId = Number(sessionStorage.getItem("activevolunteerid"));
  const studyId = Number(sessionStorage.getItem("activestudyid"));

  const {
    SelectAllQuery,
    InsertMutation,
    UpdateMutation,
    DeleteMutation,
    DecisionMutation,
  } = useStudyScreeningService({
    otherid: volunteerId,
    searchText: searchText,
    pageNumber: currentPageNumber,
    pageSize: perPageData,
    studyid: studyId,
  });

  const screeningData = SelectAllQuery.data?.data || [];
  const totalRecords = SelectAllQuery.data?.data[0]?.total_count || 0;

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Study Screening", link: STUDY_PATHS.SCREENING_LIST },
      { text: "Screening List" },
    ]);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, []);

  /* ---------------- DECISION MODAL ---------------- */

  const openDecisionModal = (row: ScreeningMaster) => {
    setSelectedScreening(row);
    if (
      volunteerstudystatus === `"passed"` ||
      volunteerstudystatus === `"failed"`
    ) {
      setDecisionStatus(volunteerstudystatus as any);
    } else {
      setDecisionStatus(null);
    }
    setReasonforunfit("");
    setIsDecisionOpen(true);
  };

  const closeDecisionModal = () => {
    setIsDecisionOpen(false);
    setSelectedScreening(null);
    setDecisionStatus(null);
    setReasonforunfit("");
  };

  const handleDecisionSave = () => {
    if (!selectedScreening || !decisionStatus) return;

    setPendingDecisionPayload({
      id: Number(selectedScreening.id),
      screeningstatus: decisionStatus,
      volunteerid: Number(sessionStorage.getItem("activevolunteerid")),
      studyid: Number(sessionStorage.getItem("activestudyid")),
      reasonforunfit: decisionStatus === "failed" ? reasonforunfit.trim() : null,
    });

    closeDecisionModal();
    setIsDecisionOpen(false);
    setIsDecisionReasonOpen(true);
  };

  const handleDecisionReasonClick = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    if (!pendingDecisionPayload) return;

    DecisionMutation.mutate(
      {
        id: pendingDecisionPayload.id,
        screeningstatus: pendingDecisionPayload.screeningstatus,
        volunteerid: pendingDecisionPayload.volunteerid,
        studyid: pendingDecisionPayload.studyid,
        updatereason: reasonPayload.reason,
        reasonforunfit: pendingDecisionPayload.reasonforunfit,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["screening-selectAll"] });
          showToastMessage(
            "success",
            res.message || "Decision updated successfully",
          );
          const targetTab =
            pendingDecisionPayload.screeningstatus === "passed"
              ? "screening_passed"
              : "screening_failed";
          setIsDecisionReasonOpen(false);
          setPendingDecisionPayload(null);
          setReasonforunfit("");
          navigate(`/study/screeninglist/`, { state: { activeTab: targetTab } });
        },
      },
    );
  };

  /* ---------------- MODAL TOGGLES ---------------- */

  const toggle = () => setIsOpen(!isOpen);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const toggleReasonModal = (): void => {
    setIsReasonModal((prev) => !prev);
  };
  const ToggleDeleteResason = (): void => {
    setShowDeleteReason((prev) => !prev);
  };

  /* ---------------- ADD ---------------- */

  const onAdd = async () => {
    setEditRecord(null);
    setIsAddLoading(true);
    try {
      await AssignCRFSelectAllQuery.refetch();
    } catch (e) {
      console.error(e);
    } finally {
      setIsAddLoading(false);
      setIsOpen(true);
    }
  };

  /* ---------------- EDIT ---------------- */

  const onEdit = (item: ScreeningMaster) => {
    setEditRecord(item);
    toggle();
  };



  /* ---------------- SAVE ---------------- */

  const handleSave = async (
    data: ScreeningFormValues | Partial<ScreeningFormValues>,
  ): Promise<void> => {
    try {
      if (editRecord && data.id) {
        setUpdatedRecord(data as ScreeningFormValues);
        toggleReasonModal();
      } else {
        const crfidArray = Array.isArray(data.crfid)
          ? data.crfid.map((id) => Number(id))
          : [Number(data.crfid)];

        InsertMutation.mutate(
          {
            volunteerid: volunteerId,
            studyscreeningname: data.studyscreeningname,
            crfidarray: crfidArray,
            crfid: crfidArray[0],
            studyid: studyId,
          },
          {
            onSuccess: (res: any) => {
              queryClient.invalidateQueries({
                queryKey: ["screening-selectAll"],
              });
              showToastMessage(
                "success",
                res.message || "Screening added successfully",
              );
              toggle();
              setSearchText(null);
              setCurrentPageNumber(1);
            },
            onError: (error: any) => {
              showToastMessage("error", error || "Failed to add screening");
            },
          },
        );
      }
    } catch (error) {
      console.error("Save error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const [volunteerstudystatus, setVolunteerstudystatus] = useState<
    string | null
  >("");
  const [isLocked, setIsLocked] = useState<boolean>(false);

  useEffect(() => {
    setVolunteerstudystatus(
      sessionStorage.getItem("active_volunteer_screening_status"),
    );
  }, [volunteerstudystatus]);
  useEffect(() => {
    setIsLocked(
      volunteerstudystatus === `"passed"` ||
      volunteerstudystatus === `"failed"`,
    );
  }, [volunteerstudystatus]);

  /* ---------------- RECORD UPDATE ---------------- */

  const btnReasonClick = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    // Handle Scenario 1 & 2: Record Info Update
    if (!editRecord || !updatedRecord) return;

    const crfidArray = Array.isArray(updatedRecord.crfid)
      ? updatedRecord.crfid.map((id) => Number(id))
      : [Number(updatedRecord.crfid)];

    UpdateMutation.mutate(
      {
        id: Number(reasonPayload.id),
        studyscreeningname: updatedRecord.studyscreeningname!,
        crfid: crfidArray[0],
        crfidarray: crfidArray,
        updatereason: reasonPayload.reason,
        studyid: studyId,
        volunteerid: volunteerId,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["screening-selectAll"] });
          showToastMessage(
            "success",
            res.message || "Screening updated successfully",
          );
          setSearchText(null);
          setCurrentPageNumber(1);
          toggle();
          toggleReasonModal();
          setEditRecord(null);
          setUpdatedRecord(null);
        },
      },
    );
  };

  /* ---------------- DELETE ---------------- */

  const handleDeleteClick = (item: ScreeningMaster) => {
    setSelectedRecord(item);
    toggleDeleteModal();
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason: string;
  }): void => {
    if (!selectedRecord) return;

    DeleteMutation.mutate(
      {
        id: Number(reasonPayload.id),
        updatereason: reasonPayload.reason,
        studyid: studyId,
        volunteerid: volunteerId,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["screening-selectAll"] });
          showToastMessage(
            "success",
            res.message || "Screening deleted successfully",
          );
          setSearchText(null);
          setCurrentPageNumber(1);
          toggleDeleteModal();
          setSelectedRecord(null);
          ToggleDeleteResason();
        },
      },
    );
  };

  /* ---------------- MAP TO FORM ---------------- */

  const mapRecordToForm = (
    record: ScreeningMaster,
  ): Partial<ScreeningFormValues> => {
    const crfidArray = record.crflist
      ? record.crflist.map((item: any) => item.crfid.toString())
      : record.crfid
        ? [record.crfid.toString()]
        : [];

    return {
      id: record.id.toString(),
      studyscreeningname: record.studyscreeningname,
      crfid: crfidArray,
      crflist: record.crflist,
    } as any;
  };

  const [activePopover, setActivePopover] = useState<string | null>(null);

  const togglePopover = (id: string) => {
    setActivePopover(activePopover === id ? null : id);
  };

  /* ---------------- CRF VIEWER ---------------- */

  const openCrf = (record: ScreeningMaster) => {
    setSelectedCrf(record);
    setIsCrfOpen(true);
    setActivePopover(null); // Close the popover when opening a CRF
  };

  const closeCrf = () => {
    setIsCrfOpen(false);
    setSelectedCrf(null);
  };

  const handleCrfSave = (answers: any) => {
    if (!selectedCrf) return;

    const crfJson = {
      ...selectedCrf.selectedCrfDetails.crfdetails,
      answers: answers,
    };

    UpdateMutation.mutate(
      {
        studyscreeningid: selectedCrf.id,
        crfid: selectedCrf.selectedCrfDetails.crfid,
        json: crfJson,
        updatereason: "CRF Answers Updated",
        studyid: studyId,
        volunteerid: volunteerId,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["screening-selectAll"] });
          showToastMessage(
            "success",
            res.message || "CRF form submitted successfully",
          );
          closeCrf();
        },
      },
    );
  };

  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null); 
  const candecide = !readonly && hasPermission(PERMISSION_MODULES.STUDY_SCREENING, PERMISSION_ACTIONS.DECIDE_SCREENING_RESULT)

  const screeningColumn = getScreeningColumns({
    activePopover,
    togglePopover,
    hoveredRowId,
    setHoveredRowId,
    openCrf,
    openDecisionModal,
    hasPermission,
    candecide,
  });

  /* ---------------- SEARCH ---------------- */

  const onSearch = (text: string | null) => {
    setSearchText(text);
    setCurrentPageNumber(1);
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Study Screening",
      studyid: studyId,
      refid: volunteerId,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File(
            [data],
            "Volunteer_Screening_Audit_Log.pdf",
            { type: "application/pdf" },
          );
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  return (
    <Container fluid className="px-0">
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div
              className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
            >
              <i className={`ri-search-line ${sf.searchIcon}`} />
              <input
                type="text"
                placeholder="Search..."
                value={searchText ?? ""}
                className={sf.searchInput}
                onChange={(e) => onSearch(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
              {searchText && (
                <i
                  className={`ri-close-line ${sf.clearIcon}`}
                  onClick={() => onSearch("")}
                />
              )}
            </div>
            <div className="d-flex gap-2 align-items-center">
               {!readonly && (
                <CommonButton
                module={PERMISSION_MODULES.STUDY_SCREENING}
                action={PERMISSION_ACTIONS.SCREENING_AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
              )}
              {!readonly && !isLocked && (
                <CommonButton
                  module={PERMISSION_MODULES.STUDY_SCREENING}
                  action={PERMISSION_ACTIONS.SCREENING_ADD}
                  onClick={onAdd}
                  disabled={isAddLoading}
                  outline
                  size="sm"
                  color="primary"
                >
                  {isAddLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="ri-add-line me-1" />
                  )}
                  Add
                </CommonButton>
              )}
            </div>
          </div>

          <CommonTableIndexWise
            moduleName={`${PERMISSION_MODULES.STUDY_SCREENING}:SCREENING`}
            columns={screeningColumn}
            data={screeningData}
            {...(!readonly && !isLocked && {
              actions: {
                onEdit,
                onDelete: handleDeleteClick,
              },
            })}
            loading={SelectAllQuery.isFetching}
            pageSize={perPageData}
            currentPage={currentPageNumber}
            onPageChange={setCurrentPageNumber}
            totalRecords={totalRecords}
          />
        </CardBody>
      </Card>

      {/* ADD / EDIT MODAL */}
      <AddEditScreeningModal
        isOpen={isOpen}
        toggle={toggle}
        editRecord={editRecord}
        mapRecordToForm={mapRecordToForm}
        onSave={handleSave}
        setSubmitHandler={setSubmitHandler}
        submitHandler={submitHandler}
        setIsSubmitting={setIsSubmitting}
        isSubmitting={isSubmitting}
        isPending={InsertMutation.isPending || UpdateMutation.isPending}
      />

      {/* CRF MODAL */}
      <CRFScreeningModal
        isOpen={isCrfOpen}
        toggle={closeCrf}
        selectedCrf={selectedCrf}
        onClose={closeCrf}
        onSave={handleCrfSave}
        isPending={UpdateMutation.isPending}
        readonly={readonly}
      />

      {/* Delete Modal */}
      {deleteModal && (
        <DeleteModal
          isopen={deleteModal}
          toggle={toggleDeleteModal}
          recordId={selectedRecord?.id ?? 0}
          recordName={selectedRecord?.studyscreeningname}
          onDeleteClick={confirmDelete}
          isLoading={DeleteMutation.isPending}
          showDeleteReason={showDeleteReason}
          toggleDeleteReason={ToggleDeleteResason}
        />
      )}

      {/* Reason Modal */}
      {isReasonModal && (
        <Reason
          isOpen={isReasonModal}
          toggle={toggleReasonModal}
          action="Update"
          id={editRecord?.id}
          click={btnReasonClick}
          isLoading={UpdateMutation.isPending}
        />
      )}

      {/* Decision Reason Modal */}
      {isDecisionReasonOpen && (
        <Reason
          isOpen={isDecisionReasonOpen}
          toggle={() => {
            setIsDecisionReasonOpen(false);
            setPendingDecisionPayload(null);
          }}
          action="Update"
          id={pendingDecisionPayload?.id}
          click={handleDecisionReasonClick}
          isLoading={DecisionMutation.isPending}
        />
      )}

      <ScreeningDecisionModal
        isOpen={isDecisionOpen}
        toggle={closeDecisionModal}
        selectedScreening={selectedScreening}
        decisionStatus={decisionStatus as any}
        setDecisionStatus={setDecisionStatus as any}
        reasonforunfit={reasonforunfit}
        setReasonforunfit={setReasonforunfit}
        onSave={handleDecisionSave}
        isPending={DecisionMutation.isPending}
      />

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </Container>
  );
};

export default StudyScreeningList;
